import { useEffect, useMemo, useState } from 'react';
import type { LapStats, Session } from '@tla/types';
import { toKmh } from '@tla/core';
import { api } from '../api/client';
import { useWorkspaceStore } from '../store/workspaceStore';
import { formatLapTime } from '../util/format';

/** One lap tagged with the driver/car/team it belongs to, for aggregation. */
interface LapEntry {
  driver: string;
  car: string;
  team: string;
  lap: LapStats;
}

/** A single "best" record: the winning value and who achieved it. */
interface Best {
  value: number;
  driver: string;
}

/** Aggregated key stats for a scope (the whole event, or one team). */
interface ScopeStats {
  fastestLap: Best | null;
  topSpeed: Best | null;
  maxLateralG: Best | null;
  maxLongitudinalG: Best | null;
  maxRpm: Best | null;
  bestSectors: (Best | null)[];
  theoreticalOptimal: number | null;
  lapCount: number;
}

/** Pick the entry with the max (or min) finite value of `valueFn`. */
function pick(entries: LapEntry[], valueFn: (l: LapStats) => number | undefined, dir: 'max' | 'min'): Best | null {
  let best: Best | null = null;
  for (const e of entries) {
    const v = valueFn(e.lap);
    if (v == null || !Number.isFinite(v)) continue;
    if (best == null || (dir === 'max' ? v > best.value : v < best.value)) {
      best = { value: v, driver: e.driver };
    }
  }
  return best;
}

function computeScope(entries: LapEntry[], sectorCount: number): ScopeStats {
  const valid = entries.filter((e) => e.lap.valid);
  const bestSectors: (Best | null)[] = [];
  for (let i = 0; i < sectorCount; i++) {
    // Best sectors (and the theoretical optimal derived from them) must come
    // from valid laps only; invalid out/in and partial laps yield spuriously
    // low sector times that never appear in the lap list.
    bestSectors.push(pick(valid, (l) => l.sectorTimes[i], 'min'));
  }
  const optimalParts = bestSectors.map((b) => (b ? b.value : null));
  const theoreticalOptimal = optimalParts.every((v) => v != null)
    ? optimalParts.reduce((a, b) => (a as number) + (b as number), 0)
    : null;
  return {
    fastestLap: pick(valid, (l) => l.lapTime, 'min'),
    topSpeed: pick(entries, (l) => l.topSpeed, 'max'),
    maxLateralG: pick(entries, (l) => l.maxLateralG, 'max'),
    maxLongitudinalG: pick(entries, (l) => l.maxLongitudinalG, 'max'),
    maxRpm: pick(entries, (l) => l.maxRpm, 'max'),
    bestSectors,
    theoreticalOptimal,
    lapCount: valid.length,
  };
}

function StatRow(props: { label: string; best: Best | null; format: (v: number) => string }): React.ReactElement {
  const { label, best, format } = props;
  return (
    <div className="stat-row">
      <span className="stat-label">{label}</span>
      {best ? (
        <>
          <span className="stat-value">{format(best.value)}</span>
          <span className="stat-driver">{best.driver}</span>
        </>
      ) : (
        <span className="stat-value muted">—</span>
      )}
    </div>
  );
}

function ScopeCard(props: { title: string; subtitle?: string; stats: ScopeStats }): React.ReactElement {
  const { title, subtitle, stats } = props;
  return (
    <div className="stat-card">
      <div className="stat-card-head">
        <span className="stat-card-title">{title}</span>
        {subtitle && <span className="small muted">{subtitle}</span>}
      </div>
      <StatRow label="Fastest lap" best={stats.fastestLap} format={formatLapTime} />
      <StatRow label="Top speed" best={stats.topSpeed} format={(v) => `${Math.round(toKmh(v))} km/h`} />
      <StatRow label="Highest lateral G" best={stats.maxLateralG} format={(v) => `${v.toFixed(2)} G`} />
      <StatRow label="Highest braking/accel G" best={stats.maxLongitudinalG} format={(v) => `${v.toFixed(2)} G`} />
      <StatRow label="Highest RPM" best={stats.maxRpm} format={(v) => `${Math.round(v)}`} />
      {stats.bestSectors.map((b, i) => (
        <StatRow key={i} label={`Best sector ${i + 1}`} best={b} format={(v) => `${v.toFixed(2)}s`} />
      ))}
      <div className="stat-row">
        <span className="stat-label">Theoretical optimal</span>
        <span className="stat-value">{formatLapTime(stats.theoreticalOptimal)}</span>
        <span className="stat-driver small muted">{stats.lapCount} valid laps</span>
      </div>
    </div>
  );
}

/**
 * Modal showing key aggregated stats for the selected event: an overall
 * leaderboard plus a per-team breakdown (fastest lap, top speed, best sector
 * driver, highest G, highest RPM and the theoretical optimal lap).
 *
 * When `teamFilter` is supplied the modal is scoped to a single team: only that
 * team's laps are aggregated and the per-team breakdown is hidden.
 */
export function EventStats(props: { onClose: () => void; teamFilter?: string }): React.ReactElement {
  const { teamFilter } = props;
  const store = useWorkspaceStore();
  const eventId = store.selectedEventId;
  const event = eventId ? store.events[eventId] : null;
  const track = event ? store.tracks[event.trackId] : null;
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!eventId) return;
    void (async () => {
      setLoading(true);
      const rows = await api.listSessions(eventId);
      if (cancelled) return;
      setSessions(rows);
      await Promise.all(rows.map((s) => store.ensureSummary(s.id)));
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const summaries = store.summaries;

  const { overall, teams, sectorCount } = useMemo(() => {
    const entries: LapEntry[] = [];
    let sectors = 0;
    for (const s of sessions) {
      const summary = summaries[s.id];
      if (!summary) continue;
      sectors = Math.max(sectors, summary.bestSectorTimes.length);
      const driver = store.drivers[s.driverId]?.name ?? 'Unknown';
      const carEntry = store.cars[s.carId];
      const car = carEntry?.name ?? '—';
      const team = s.team ?? '';
      if (teamFilter && team !== teamFilter) continue;
      for (const lap of summary.laps) entries.push({ driver, car, team, lap });
    }
    const overallStats = computeScope(entries, sectors);
    // In team-scoped mode we only show the single overall (team) card.
    if (teamFilter) {
      return { overall: overallStats, teams: [] as { team: string; stats: ScopeStats }[], sectorCount: sectors };
    }
    const byTeam = new Map<string, LapEntry[]>();
    for (const e of entries) {
      if (!e.team) continue;
      const list = byTeam.get(e.team) ?? [];
      list.push(e);
      byTeam.set(e.team, list);
    }
    const teamStats = [...byTeam.entries()]
      .map(([team, list]) => ({ team, stats: computeScope(list, sectors) }))
      .sort((a, b) => {
        const av = a.stats.fastestLap?.value ?? Infinity;
        const bv = b.stats.fastestLap?.value ?? Infinity;
        return av - bv;
      });
    return { overall: overallStats, teams: teamStats, sectorCount: sectors };
  }, [sessions, summaries, store.drivers, store.cars, teamFilter]);

  return (
    <div className="overlay" onClick={props.onClose}>
      <div className="dialog stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stats-head">
          <div>
            <h2 style={{ margin: 0 }}>
              {teamFilter ? teamFilter : (event?.name ?? 'Event stats')}
            </h2>
            {event && (
              <div className="small muted">
                {teamFilter ? `${event.name} · ` : ''}
                {event.date} · {track?.name ?? 'Unknown track'} · {sessions.length} sessions
              </div>
            )}
          </div>
          <button className="icon-btn" onClick={props.onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {loading ? (
          <div className="small muted" style={{ padding: 16 }}>
            Loading session summaries…
          </div>
        ) : (
          <div className="stats-grid">
            <ScopeCard
              title={teamFilter ? teamFilter : 'Event overall'}
              subtitle={teamFilter ? 'Team' : `${sectorCount} sectors`}
              stats={overall}
            />
            {teams.map(({ team, stats }) => (
              <ScopeCard key={team} title={team} subtitle="Team" stats={stats} />
            ))}
            {!teamFilter && teams.length === 0 && (
              <div className="small muted" style={{ padding: 8 }}>
                No teams assigned — set a team on cars (in Import) to see a per-team breakdown.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
